import React, {useContext, useEffect, useMemo} from 'react';
import {Workspace} from './types/workspace';
import {
  PersonalWorkspace,
  personalWorkspaceIdOf,
  useUserWorkspaces,
} from './user-workspaces';
import {
  setActiveWorkspaceEncrypted,
  setActiveWorkspaceId,
} from './active-workspace-id';
import {useCookie} from '@ui/utils/hooks/use-cookie';

export interface ActiveWorkspaceIdContextValue {
  workspaceId: number | null;
  setWorkspaceId: (id: number) => void;
}

// add default context value so it does not error out, if there's no context provider
export const ActiveWorkspaceIdContext =
  React.createContext<ActiveWorkspaceIdContextValue>({
    // set default as null, so it's not sent via query params in admin and
    // other places if component is not wrapped in workspace context explicitly
    workspaceId: null,
    setWorkspaceId: () => {},
  });

export function useActiveWorkspaceId(): ActiveWorkspaceIdContextValue {
  return useContext(ActiveWorkspaceIdContext);
}

export function useActiveWorkspace(): Workspace | null | undefined {
  const {workspaceId} = useActiveWorkspaceId();
  const query = useUserWorkspaces();
  if (query.data) {
    return query.data.find(workspace => workspace.id === workspaceId);
  }
  return null;
}

/**
 * Whether the active workspace has been manually disabled by its owner.
 * When true the whole workspace is read-only: the owner may browse the
 * listing but every access/write affordance (preview, download, comment,
 * tag, upload, edit) must be suppressed. The server enforces the same.
 */
export function useActiveWorkspaceIsDisabled(): boolean {
  return !!useActiveWorkspace()?.manually_disabled;
}

interface ActiveWorkspaceProviderProps {
  children: any;
}
export function ActiveWorkspaceProvider({
  children,
}: ActiveWorkspaceProviderProps) {
  const [workspaceId, setCookieId] = useCookie(
    'activeWorkspaceId',
    `${PersonalWorkspace.id}`,
  );
  const workspaces = useUserWorkspaces();
  const parsedId = parseInt(workspaceId);

  // Once the encrypted personal workspace exists it IS "My Drive": move the
  // user off the legacy implicit drive (id 0), and back to My Drive if the
  // remembered workspace is gone (left / deleted / never theirs).
  useEffect(() => {
    if (!workspaces.data || workspaces.isPlaceholderData) return;
    const personalId = personalWorkspaceIdOf(workspaces.data);
    if (!personalId) return;
    const known = workspaces.data.some(w => w.id === parsedId);
    if (parsedId === 0 || !known) {
      setCookieId(`${personalId}`);
    }
  }, [parsedId, workspaces.data, workspaces.isPlaceholderData, setCookieId]);

  useEffect(() => {
    setActiveWorkspaceId(parsedId);
    const ws = workspaces.data?.find(w => w.id === parsedId);
    setActiveWorkspaceEncrypted(!!ws?.encrypted);
    // clear workspace id when unmounting workspace provider
    return () => {
      setActiveWorkspaceId(0);
      setActiveWorkspaceEncrypted(false);
    };
  }, [parsedId, workspaces.data]);

  const contextValue: ActiveWorkspaceIdContextValue = useMemo(() => {
    return {
      workspaceId: parseInt(workspaceId),
      setWorkspaceId: (id: number) => {
        setCookieId(`${id}`);
      },
    };
  }, [workspaceId, setCookieId]);

  return (
    <ActiveWorkspaceIdContext.Provider value={contextValue}>
      {children}
    </ActiveWorkspaceIdContext.Provider>
  );
}
